import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import { CSSObject, styled, Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { AppStoreButton, GooglePlayButton } from 'react-mobile-app-button';
import { useLocation, useNavigate } from 'react-router-dom';
import logo from 'src/assets/images/logos/logo-with-text_dark-148.png';
import {
  BRAND_NAVIGATION_CLASS,
  navigationCssVars,
  useBranding,
} from 'src/branding';
import useAppSelector from 'src/hooks/Redux/useAppSelector';
import { selectCurrentUser } from 'src/redux/slices/loggedUserSlice';
import { currentUser } from 'src/redux/slices/userSlice';
import { Circles } from './Circles/Circles';
import { APKUrl, IOSUrl, Route, ROUTES, ROUTES_NO_AUTH } from './const';
import styles from './Sidebar.module.scss';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  position: 'relative',
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': {
      ...openedMixin(theme),
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': {
      ...closedMixin(theme),
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
    },
  }),
}));

export const Sidebar = () => {
  const location = useLocation();
  const isWideScreen = useMediaQuery('(min-width:980px)');
  const isMobile = useMediaQuery('(min-width:545px)');
  const isNarrowMobile = useMediaQuery('(max-width:545px)');
  const [open, setOpen] = useState(isWideScreen);
  const user = useAppSelector(currentUser);
  const navigate = useNavigate();
  const loggedInUser = useAppSelector(selectCurrentUser);
  // Stays Dunbar unless the open circle asked for a branded navigation
  const branding = useBranding();
  const [expandedRoutes, setExpandedRoutes] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    setOpen(isWideScreen);
  }, [isWideScreen]);

  useEffect(() => {
    // Auto-expand any parent route whose child matches the current path
    const nextExpanded: Record<string, boolean> = {};
    routes.forEach((route) => {
      if (route.children?.length) {
        const hasActiveChild = route.children.some(
          (child) => location.pathname === child.path
        );
        if (hasActiveChild) {
          nextExpanded[route.title] = true;
        }
      }
    });
    if (Object.keys(nextExpanded).length) {
      setExpandedRoutes((prev) => ({ ...prev, ...nextExpanded }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleNavigation = (path: string) => {
    if (path?.includes('https://')) {
      window.open(path);
      return;
    }

    navigate(path);
  };

  const filteredRoutes =
    loggedInUser?.role === 'ADMIN'
      ? ROUTES
      : ROUTES.filter((route) => route.adminOnly !== true);

  const routes = user ? filteredRoutes : ROUTES_NO_AUTH;

  const hideSidebar =
    location.pathname.includes('edit-circle') ||
    location.pathname.includes('create-circle') ||
    location.pathname.includes('edit-profile');

  const drawerContentClass = classNames({
    [styles.drawerContent]: true,
    [styles.openDrawer]: open,
    [styles.closedDrawer]: !open,
  });

  const getIcon = (route: Route, isActive: boolean) => {
    if (isActive && route.activeIcon) {
      return route.activeIcon;
    }
    return route.icon;
  };

  const isRouteActive = (route: Route) => {
    if (route.children?.length) {
      return route.children.some((child) => location.pathname === child.path);
    }
    return location.pathname === route.path;
  };

  const toggleExpanded = (routeTitle: string) => {
    setExpandedRoutes((prev) => ({
      ...prev,
      [routeTitle]: !prev[routeTitle],
    }));
  };

  if (isNarrowMobile) {
    return null;
  }

  return (
    <Box
      className={classNames(styles.sidebarContainer, {
        [styles.brandNav]: branding.brandNavigation,
        [BRAND_NAVIGATION_CLASS]: branding.brandNavigation,
      })}
      style={{
        display: hideSidebar && !isMobile ? 'none' : 'flex',
        ...navigationCssVars(branding),
      }}
    >
      <CssBaseline />
      <Drawer variant="permanent" open={open}>
        <img className={styles.logo} src={logo} alt="logo-with-text" />
        <div className={drawerContentClass}>
          <List className={styles.navList}>
            {routes.map((route) => {
              const isActive = isRouteActive(route);
              const isExpanded = expandedRoutes[route.title] ?? false;

              return (
                <div key={route.title}>
                  <ListItem disablePadding className={styles.navItem}>
                    <ListItemButton
                      className={styles.navButton}
                      component="a"
                      href={route.path}
                      onClick={(e) => {
                        if (
                          e.ctrlKey ||
                          e.metaKey ||
                          e.shiftKey ||
                          e.altKey ||
                          e.button !== 0
                        ) {
                          return;
                        }

                        e.preventDefault();

                        if (route.children?.length) {
                          toggleExpanded(route.title);
                          return;
                        }

                        handleNavigation(route.path);
                      }}
                    >
                      <ListItemIcon
                        className={styles.navIcon}
                        data-route={route.title}
                      >
                        {getIcon(route, isActive)}
                      </ListItemIcon>
                      <div
                        className={styles.text}
                        data-active={isActive.toString()}
                        data-route={route.title}
                      >
                        {route.title}
                      </div>
                      {route.children?.length && open && (
                        <ListItemIcon className={styles.navIcon}>
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </ListItemIcon>
                      )}
                    </ListItemButton>
                  </ListItem>

                  {route.children?.length && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List
                        component="div"
                        disablePadding
                        className={styles.subNavList}
                      >
                        {route.children.map((child) => {
                          const childIsActive =
                            location.pathname === child.path;

                          return (
                            <ListItem
                              key={child.title}
                              disablePadding
                              className={styles.navItem}
                            >
                              <ListItemButton
                                className={classNames(
                                  styles.navButton,
                                  styles.subNavButton
                                )}
                                component="a"
                                href={child.path}
                                onClick={(e) => {
                                  if (
                                    e.ctrlKey ||
                                    e.metaKey ||
                                    e.shiftKey ||
                                    e.altKey ||
                                    e.button !== 0
                                  ) {
                                    return;
                                  }

                                  e.preventDefault();
                                  handleNavigation(child.path);
                                }}
                              >
                                <ListItemIcon
                                  className={styles.navIcon}
                                  data-route={child.title}
                                >
                                  {getIcon(child, childIsActive)}
                                </ListItemIcon>
                                <div
                                  className={classNames(
                                    styles.text,
                                    styles.subText
                                  )}
                                  data-active={childIsActive.toString()}
                                  data-route={child.title}
                                >
                                  {child.title}
                                </div>
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </div>
              );
            })}
          </List>

          {user && (
            <div className={styles.userSection}>
              <Circles />
            </div>
          )}

          <div className={styles.buttons}>
            <GooglePlayButton
              url={APKUrl}
              theme={'dark'}
              className={styles.linkButton}
            />
            <AppStoreButton
              url={IOSUrl}
              theme={'dark'}
              className={styles.linkButton}
            />
          </div>
        </div>
      </Drawer>
    </Box>
  );
};
